import { useEffect, useRef } from 'react'

/**
 * Lehký konfetový/ohňostrojový efekt (canvas, bez závislosti) — jednorázový výstřel při mountu.
 * Respektuje prefers-reduced-motion (pak nic nevykreslí). Sám se po doběhnutí zastaví.
 * Použití: {podmínka && <Confetti />}
 */
export default function Confetti({ duration = 2800 }: { duration?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const W = (canvas.width = window.innerWidth * dpr)
    const H = (canvas.height = window.innerHeight * dpr)
    canvas.style.width = window.innerWidth + 'px'
    canvas.style.height = window.innerHeight + 'px'

    const colors = ['#6366f1', '#06b6d4', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6']
    const N = 150
    const parts = Array.from({ length: N }, () => {
      const speed = (5 + Math.random() * 9) * dpr
      const ang = Math.PI * (1.1 + Math.random() * 0.8) // vzhůru do stran
      return {
        x: W / 2 + (Math.random() - 0.5) * W * 0.35,
        y: H * 0.62,
        vx: Math.cos(ang) * speed * (Math.random() < 0.5 ? 1 : -1),
        vy: Math.sin(ang) * speed - 3 * dpr,
        size: (4 + Math.random() * 5) * dpr,
        color: colors[(Math.random() * colors.length) | 0],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.35,
      }
    })

    const g = 0.25 * dpr
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const t = now - start
      ctx.clearRect(0, 0, W, H)
      const life = Math.max(0, 1 - t / duration)
      for (const p of parts) {
        p.vy += g
        p.x += p.vx
        p.y += p.vy
        p.vx *= 0.99
        p.rot += p.vr
        ctx.globalAlpha = life
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.62)
        ctx.restore()
      }
      if (t < duration) raf = requestAnimationFrame(tick)
      else ctx.clearRect(0, 0, W, H)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [duration])

  return <canvas ref={canvasRef} className="fixed inset-0 z-[200] pointer-events-none" aria-hidden="true" />
}
